import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import axios from "axios";
import { debounce } from "lodash";
import {
  Modal,
  Button,
  Form,
  Tabs,
  Tab,
  Table,
  Spinner,
  Badge,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import AuthContext from "../context/AuthContext";
import Pagination from "./common/Pagination";

const API_URL = process.env.REACT_APP_API_URL;
const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL;

// ⭐ Mapping hạng: ID -> Tên (không cần fetch từ API)
const LEVEL_MAP = {
  7: { name: "G", label: "Hạng G" },
  1: { name: "F", label: "Hạng F" },
  2: { name: "E", label: "Hạng E" },
  3: { name: "D", label: "Hạng D" },
  4: { name: "C", label: "Hạng C" },
  5: { name: "B", label: "Hạng B" },
  6: { name: "A", label: "Hạng A" },
};

const formatPercent = (value) => `${Math.round((Number(value) || 0) * 100)}%`;
const formatMatchCount = (value) =>
  Number(value || 0).toLocaleString("vi-VN", { maximumFractionDigits: 1 });

const PlayerList = () => {
  const { user } = useContext(AuthContext);
  const [players, setPlayers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("");
  const limit = 10;

  // State for Modal and Form
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState({
    id: null,
    name: "",
    date_of_birth: "",
    phone: "",
    level_start: 7,
    level_present: 7,
    level_point: 0,
    status: 1,
    image: null,
    vertical_image: null,
  });

  // State cho upload ảnh
  const [portraitFile, setPortraitFile] = useState(null);
  const [idCardFile, setIdCardFile] = useState(null);
  const [portraitPreview, setPortraitPreview] = useState(null);
  const [idCardPreview, setIdCardPreview] = useState(null);

  // State for Ranking Review Tab
  const [reviewList, setReviewList] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewTotalPages, setReviewTotalPages] = useState(1);
  const [key, setKey] = useState("list");
  const [pendingList, setPendingList] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingTotalPages, setPendingTotalPages] = useState(1);
  const [pendingSearchTerm, setPendingSearchTerm] = useState("");

  const [adjustmentSeason, setAdjustmentSeason] = useState("");
  const [playerAdjustmentStats, setPlayerAdjustmentStats] = useState({
    old_players: [],
    new_players: [],
  });
  const [adjustmentStatsTab, setAdjustmentStatsTab] = useState("old_players");
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [adjustmentApplying, setAdjustmentApplying] = useState(false);
  const [selectedAdjustmentIds, setSelectedAdjustmentIds] = useState(() => new Set());
  const [adjustmentMessage, setAdjustmentMessage] = useState(null);
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [adjustmentHistoryLoading, setAdjustmentHistoryLoading] = useState(false);

  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const res = await axios.get(`${API_URL}/seasons`);
        setSeasons(res.data);
        if (res.data?.length > 0) {
          setAdjustmentSeason((prev) => prev || String(res.data[0].id));
        }
      } catch (error) {
        console.error("Error fetching seasons:", error);
      }
    };

    fetchSeasons();
  }, []);

  const fetchPlayers = useCallback(async () => {
    try {
      const countParams = new URLSearchParams({ search: searchTerm });
      const listParams = new URLSearchParams({
        page,
        limit,
        search: searchTerm,
        sortBy: "id",
        sortOrder: "ASC",
      });
      if (selectedSeason) {
        countParams.set("season_id", selectedSeason);
        listParams.set("season_id", selectedSeason);
      }

      const countRes = await axios.get(`${API_URL}/players/count?${countParams}`);
      const totalPlayers = countRes.data.total;
      setTotalPages(Math.ceil(totalPlayers / limit) || 1);

      const playersRes = await axios.get(`${API_URL}/players?${listParams}`);
      setPlayers(playersRes.data);
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  }, [page, searchTerm, selectedSeason]);

  const debouncedFetchPlayers = useMemo(
    () => debounce(fetchPlayers, 300),
    [fetchPlayers]
  );

  const fetchReviewList = useCallback(async () => {
    setReviewLoading(true);
    try {
      const countParams = new URLSearchParams();
      const listParams = new URLSearchParams({ page: reviewPage, limit });
      if (selectedSeason) {
        countParams.set("season_id", selectedSeason);
        listParams.set("season_id", selectedSeason);
      }

      const countQuery = countParams.toString();
      const countRes = await axios.get(
        `${API_URL}/players/ranking-review/count${countQuery ? `?${countQuery}` : ""}`
      );
      const totalReviews = countRes.data.total;
      setReviewTotalPages(Math.ceil(totalReviews / limit) || 1);

      const res = await axios.get(`${API_URL}/players/ranking-review?${listParams}`);
      setReviewList(res.data);
    } catch (error) {
      console.error("Error fetching ranking review list:", error);
    } finally {
      setReviewLoading(false);
    }
  }, [reviewPage, selectedSeason]);

  const fetchPendingList = useCallback(async () => {
    setPendingLoading(true);
    try {
      const countRes = await axios.get(
        `${API_URL}/players/pending/count?search=${pendingSearchTerm}`
      );
      const totalPending = countRes.data.total;
      setPendingTotalPages(Math.ceil(totalPending / limit) || 1);

      const res = await axios.get(
        `${API_URL}/players/pending/list?page=${pendingPage}&limit=${limit}&search=${pendingSearchTerm}`
      );
      setPendingList(res.data);
    } catch (error) {
      console.error("Error fetching pending players:", error);
    } finally {
      setPendingLoading(false);
    }
  }, [pendingPage, pendingSearchTerm]);

  const fetchAdjustmentStats = useCallback(async () => {
    if (!adjustmentSeason || user?.role !== "admin") {
      setPlayerAdjustmentStats({ old_players: [], new_players: [] });
      return;
    }
    setAdjustmentLoading(true);
    setAdjustmentMessage(null);
    try {
      const res = await axios.get(
        `${API_URL}/rosters/player-adjustment-stats?season_id=${adjustmentSeason}`,
      );
      setPlayerAdjustmentStats({
        old_players: res.data?.old_players || [],
        new_players: res.data?.new_players || [],
      });
      setSelectedAdjustmentIds(new Set());
    } catch (error) {
      console.error("Error fetching player adjustment stats:", error);
      setPlayerAdjustmentStats({ old_players: [], new_players: [] });
      setAdjustmentMessage({ variant: "danger", text: "Không tải được danh sách điều chỉnh." });
    } finally {
      setAdjustmentLoading(false);
    }
  }, [adjustmentSeason, user]);

  const fetchAdjustmentHistory = useCallback(async () => {
    if (!adjustmentSeason || user?.role !== "admin") {
      setAdjustmentHistory([]);
      return;
    }
    setAdjustmentHistoryLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/rosters/adjustment-history?season_id=${adjustmentSeason}`,
      );
      setAdjustmentHistory(res.data || []);
    } catch (error) {
      console.error("Error fetching adjustment history:", error);
      setAdjustmentHistory([]);
    } finally {
      setAdjustmentHistoryLoading(false);
    }
  }, [adjustmentSeason, user]);

  const applyWinrateAdjustments = async (playerIds) => {
    if (!adjustmentSeason || !playerIds?.length) return;
    setAdjustmentApplying(true);
    setAdjustmentMessage(null);
    try {
      const res = await axios.post(`${API_URL}/rosters/apply-winrate-adjustments`, {
        season_id: Number(adjustmentSeason),
        player_ids: playerIds,
      });
      if (res.data?.stats) {
        setPlayerAdjustmentStats({
          old_players: res.data.stats.old_players || [],
          new_players: res.data.stats.new_players || [],
        });
      } else {
        await fetchAdjustmentStats();
      }
      setSelectedAdjustmentIds(new Set());
      const appliedCount = res.data?.applied?.length || 0;
      const skippedCount = res.data?.skipped?.length || 0;
      setAdjustmentMessage({
        variant: appliedCount > 0 ? "success" : "warning",
        text: `Đã áp dụng ${appliedCount} VĐV${skippedCount ? `, bỏ qua ${skippedCount}` : ""}.`,
      });
    } catch (error) {
      console.error("Error applying adjustments:", error);
      setAdjustmentMessage({
        variant: "danger",
        text: error.response?.data || "Lỗi khi áp dụng điểm.",
      });
    } finally {
      setAdjustmentApplying(false);
    }
  };

  const toggleAdjustmentSelection = (playerId, checked) => {
    setSelectedAdjustmentIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(Number(playerId));
      else next.delete(Number(playerId));
      return next;
    });
  };

  const eligibleAdjustmentPlayers = useMemo(() => {
    const list = playerAdjustmentStats[adjustmentStatsTab] || [];
    return list.filter((p) => !p.not_ready_yet);
  }, [playerAdjustmentStats, adjustmentStatsTab]);

  const allEligibleSelected =
    eligibleAdjustmentPlayers.length > 0 &&
    eligibleAdjustmentPlayers.every((p) => selectedAdjustmentIds.has(Number(p.id)));

  useEffect(() => {
    if (key === "list") {
      debouncedFetchPlayers();
    } else if (key === "ranking") {
      fetchReviewList();
    } else if (key === "pending") {
      fetchPendingList();
      fetchReviewList();
    } else if (key === "winrate") {
      fetchAdjustmentStats();
      fetchAdjustmentHistory();
    }
  }, [key, debouncedFetchPlayers, fetchReviewList, fetchPendingList, fetchAdjustmentStats, fetchAdjustmentHistory]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleSeasonChange = (event) => {
    setSelectedSeason(event.target.value);
    setPage(1);
    setReviewPage(1);
  };

  // Modal handlers
  const handleClose = () => {
    setShowModal(false);
    setCurrentPlayer({
      id: null,
      name: "",
      date_of_birth: "",
      phone: "",
      level_start: 7,
      level_present: 7,
      level_point: 0,
      status: 1,
      image: null,
      vertical_image: null,
    });
    setPortraitFile(null);
    setIdCardFile(null);
    setPortraitPreview(null);
    setIdCardPreview(null);
  };

  const handleShowAdd = () => {
    setIsEditing(false);
    setCurrentPlayer({
      id: null,
      name: "",
      date_of_birth: "",
      phone: "",
      level_start: 7,
      level_present: 7,
      level_point: 0,
      status: 1,
      image: null,
      vertical_image: null,
    });
    setPortraitFile(null);
    setIdCardFile(null);
    setPortraitPreview(null);
    setIdCardPreview(null);
    setShowModal(true);
  };

  const handleShowEdit = (player) => {
    setIsEditing(true);
    setCurrentPlayer(player);
    setPortraitFile(null);
    setIdCardFile(null);
    setPortraitPreview(null);
    setIdCardPreview(null);
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setCurrentPlayer((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ⭐ Handler thay đổi hạng bắt đầu - cũng thay level_present
  const handleLevelStartChange = (e) => {
    const levelValue = parseInt(e.target.value);
    setCurrentPlayer((prev) => ({
      ...prev,
      level_start: levelValue,
      level_present: levelValue,
    }));
  };

  // ⭐ Handler thay đổi hạng hiện tại khi edit - reset level_point = 0
  const handleLevelPresentChange = (e) => {
    const levelValue = parseInt(e.target.value);
    setCurrentPlayer((prev) => ({
      ...prev,
      level_present: levelValue,
      level_point: 0,
    }));
  };

  // Handler upload ảnh chân dung
  const handlePortraitChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPortraitFile(file);
      const previewUrl = URL.createObjectURL(file);
      setPortraitPreview(previewUrl);
    }
  };

  // Handler upload ảnh CCCD
  const handleIdCardChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIdCardFile(file);
      const previewUrl = URL.createObjectURL(file);
      setIdCardPreview(previewUrl);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        // Nếu có upload ảnh mới, dùng FormData
        if (portraitFile || idCardFile) {
          const formData = new FormData();
          formData.append("name", currentPlayer.name);
          formData.append("date_of_birth", currentPlayer.date_of_birth);
          formData.append("phone", currentPlayer.phone);
          formData.append("level_point", currentPlayer.level_point || 0);
          formData.append("level_start", currentPlayer.level_start);
          formData.append("level_present", currentPlayer.level_present);
          formData.append("status", currentPlayer.status);

          if (portraitFile) {
            formData.append("portrait", portraitFile);
          }
          if (idCardFile) {
            formData.append("idCard", idCardFile);
          }

          await axios.put(`${API_URL}/players/${currentPlayer.id}/with-images`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
        } else {
          // Không có ảnh mới, chỉ update thông tin
          await axios.put(`${API_URL}/players/${currentPlayer.id}`, currentPlayer);
        }
      } else {
        if (!portraitFile || !idCardFile) {
          alert("Vui lòng upload cả ảnh chân dung và ảnh CCCD");
          return;
        }

        const formData = new FormData();
        formData.append("name", currentPlayer.name);
        formData.append("date_of_birth", currentPlayer.date_of_birth);
        formData.append("phone", currentPlayer.phone);
        formData.append("level_point", 0);
        formData.append("level_start", currentPlayer.level_start);
        formData.append("level_present", currentPlayer.level_start);
        formData.append("status", currentPlayer.status);
        formData.append("portrait", portraitFile);
        formData.append("idCard", idCardFile);

        await axios.post(`${API_URL}/players/with-images`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      fetchPlayers();
      handleClose();
    } catch (error) {
      console.error("Error submitting player:", error);
      alert(error.response?.data?.error || "Có lỗi xảy ra khi lưu dữ liệu");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa vận động viên này?")) {
      try {
        await axios.delete(`${API_URL}/players/${id}`);
        fetchPlayers();
      } catch (error) {
        console.error("Error deleting player:", error);
      }
    }
  };

  // ⭐ Toggle switch cho status
  const handleStatusToggle = async (player) => {
    const updatedPlayer = {
      ...player,
      status: player.status === 1 ? 0 : 1,
    };
    try {
      await axios.put(`${API_URL}/players/${player.id}`, updatedPlayer);
      fetchPlayers();
    } catch (error) {
      console.error("Error updating player status:", error);
    }
  };



  const handleReviewRanking = async (playerId) => {
    if (
      window.confirm("Bạn có chắc chắn muốn xét hạng cho vận động viên này?")
    ) {
      try {
        await axios.put(`${API_URL}/players/ranking-review/${playerId}/approve`, {
          season_id: selectedSeason || undefined,
        });
        fetchReviewList();
      } catch (error) {
        console.error("Error reviewing ranking:", error);
        alert("Có lỗi xảy ra khi xét hạng.");
      }
    }
  };

  const canEditStatus = user && (user.role === "admin" || user.role === "bks");

  return (
    <div className="container-fluid mt-4">
      <Tabs
        id="player-tabs"
        activeKey={key}
        onSelect={(k) => setKey(k)}
        className="mb-3"
      >
        {/* Tab danh sách vận động viên */}
        <Tab eventKey="list" title="Danh sách VĐV">
          <div className="mb-3">
            <div className="row">
              <div className="col-md-6">
                <Form.Control
                  type="text"
                  placeholder="Tìm kiếm theo tên..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="col-md-3 mt-2 mt-md-0">
                <Form.Select value={selectedSeason} onChange={handleSeasonChange}>
                  <option value="">Tất cả mùa giải</option>
                  {seasons.map((season) => (
                    <option key={season.id} value={season.id}>
                      {season.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-3 text-end mt-2 mt-md-0">
                {user && (user.role === "admin" || user.role === "manager") && (
                  <Button variant="primary" onClick={handleShowAdd}>
                    + Thêm VĐV mới
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Tên</th>
                  <th>Năm sinh</th>
                  <th>Điện thoại</th>
                  <th>Hạng</th>
                  <th>Trạng thái</th>
                  <th>Hành động</th>
                </tr>
              </thead>
              <tbody>
                {players.map((player) => (
                  <tr key={player.id}>
                    <td>{player.id}</td>
                    <td>
                      <a
                        href={`https://bongbanhadong.com/players/${player.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', color: '#0d6efd', cursor: 'pointer' }}
                        title="Xem profile VĐV"
                      >
                        {player.name}
                      </a>
                    </td>
                    <td>{player.date_of_birth}</td>
                    <td>{player.phone}</td>
                    <td>
                      <strong>
                        {LEVEL_MAP[player.level_present]?.name || "?"}
                      </strong>
                    </td>
                    <td>
                      {canEditStatus ? (
                        <Form.Check
                          type="switch"
                          id={`switch-${player.id}`}
                          checked={player.status === 1}
                          onChange={() => handleStatusToggle(player)}
                          label={
                            player.status === 1 ? "Đã duyệt" : "Chưa duyệt"
                          }
                        />
                      ) : player.status === 1 ? (
                        <Badge bg="success">Đã duyệt</Badge>
                      ) : (
                        <Badge bg="warning">Chưa duyệt</Badge>
                      )}
                    </td>
                    <td>
                      {user && (user.role === "admin" || user.role === "bks") && (
                        <Button
                          variant="info"
                          size="sm"
                          onClick={() => handleShowEdit(player)}
                          className="me-2"
                        >
                          Sửa
                        </Button>
                      )}
                      {user && user.role === "admin" && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(player.id)}
                        >
                          Xóa
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </Tab>

        {/* Tab xét hạng */}
        <Tab eventKey="ranking" title="Xét hạng">
          <div className="mb-3 mt-3 row">
            <div className="col-md-4">
              <Form.Select value={selectedSeason} onChange={handleSeasonChange}>
                <option value="">Tất cả mùa giải</option>
                {seasons.map((season) => (
                  <option key={season.id} value={season.id}>
                    {season.name}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
          {reviewLoading ? (
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Đang tải...</span>
              </Spinner>
            </div>
          ) : reviewList.length === 0 ? (
            <p className="text-center text-muted">Không có VĐV cần xét hạng</p>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Tên VĐV</th>
                      <th>Đội</th>
                      <th>Hạng hiện tại</th>
                      <th>Điểm hiện tại</th>
                      <th>Hạng đề xuất</th>
                      <th>Trạng thái</th>
                      <th>Hành động</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewList.map((player) => (
                      <tr key={player.id}>					  
                        <td>
						<a
                        href={`https://bongbanhadong.com/players/${player.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', color: '#0d6efd', cursor: 'pointer' }}
                        title="Xem profile VĐV"
                      >{player.name}</a></td>
                        <td>{player.team_names || <span className="text-muted">Chưa có đội</span>}</td>
                        <td>{player.level_name}</td>
                        <td>{player.level_point}</td>
                        <td>{player.suggested_level_name}</td>
                        <td>
                          <Badge bg="info">{player.status}</Badge>
                        </td>
                        <td>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleReviewRanking(player.id)}
                          >
                            Xét hạng
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <Pagination
                currentPage={reviewPage}
                totalPages={reviewTotalPages}
                onPageChange={setReviewPage}
              />
            </>
          )}
        </Tab>

        {/* Tab VĐV chưa duyệt */}
        <Tab eventKey="pending" title={<>Chưa duyệt <Badge bg="danger" className="ms-1">{pendingList.length > 0 ? pendingList.length : ''}</Badge></>}>
          <div className="mb-3 mt-3">
            <Form.Control
              type="text"
              placeholder="Tìm kiếm VĐV chưa duyệt..."
              value={pendingSearchTerm}
              onChange={(e) => { setPendingSearchTerm(e.target.value); setPendingPage(1); }}
            />
          </div>
          {pendingLoading ? (
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Đang tải...</span>
              </Spinner>
            </div>
          ) : pendingList.length === 0 ? (
            <p className="text-center text-muted">Không có VĐV chưa duyệt</p>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Tên</th>
                      <th>Năm sinh</th>
                      <th>Điện thoại</th>
                      <th>Hạng</th>
                      <th>Đội đã thêm</th>
                      <th>Người tạo</th>
                      <th>Người cập nhật</th>
                      <th>Hành động</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingList.map((player) => (
                      <tr key={player.id}>
                        <td>{player.id}</td>
                        <td>
                          <a
                            href={`https://bongbanhadong.com/players/${player.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none', color: '#0d6efd' }}
                          >
                            {player.name}
                          </a>
                        </td>
                        <td>{player.date_of_birth}</td>
                        <td>{player.phone}</td>
                        <td><strong>{LEVEL_MAP[player.level_present]?.name || '?'}</strong></td>
                        <td>{player.team_names || <span className="text-muted">Chưa có đội</span>}</td>
                        <td>{player.created_by_name || player.created_by || <span className="text-muted">-</span>}</td>
                        <td>{player.updated_by_name || player.updated_by || <span className="text-muted">-</span>}</td>
                        <td>
                          {canEditStatus && (
                            <Button
                              variant="success"
                              size="sm"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API_URL}/players/${player.id}`, { ...player, status: 1 });
                                  fetchPendingList();
                                  fetchPlayers();
                                } catch (err) { console.error(err); }
                              }}
                            >
                              ✅ Duyệt
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <Pagination
                currentPage={pendingPage}
                totalPages={pendingTotalPages}
                onPageChange={setPendingPage}
              />
            </>
          )}
        </Tab>

        {user?.role === "admin" && (
          <Tab eventKey="winrate" title="% thắng/thua">
            <div className="mb-3 mt-3 row align-items-end g-2">
              <div className="col-md-4">
                <Form.Label className="small text-muted mb-1">Mùa giải</Form.Label>
                <Form.Select
                  value={adjustmentSeason}
                  onChange={(e) => {
                    setAdjustmentSeason(e.target.value);
                    setSelectedAdjustmentIds(new Set());
                  }}
                >
                  <option value="">-- Chọn mùa --</option>
                  {seasons.map((season) => (
                    <option key={season.id} value={season.id}>
                      {season.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-8 text-md-end">
                <Button
                  variant="primary"
                  size="sm"
                  className="me-2"
                  disabled={
                    adjustmentApplying ||
                    selectedAdjustmentIds.size === 0 ||
                    !adjustmentSeason
                  }
                  onClick={() =>
                    applyWinrateAdjustments(Array.from(selectedAdjustmentIds))
                  }
                >
                  {adjustmentApplying ? "Đang áp dụng…" : `Áp dụng đã chọn (${selectedAdjustmentIds.size})`}
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={adjustmentApplying || !adjustmentSeason}
                  onClick={() =>
                    applyWinrateAdjustments(
                      eligibleAdjustmentPlayers.map((p) => p.id),
                    )
                  }
                >
                  Áp dụng tất cả chưa cộng/trừ
                </Button>
              </div>
            </div>

            {adjustmentMessage && (
              <Alert
                variant={adjustmentMessage.variant}
                onClose={() => setAdjustmentMessage(null)}
                dismissible
              >
                {adjustmentMessage.text}
              </Alert>
            )}

            <div className="text-muted small mb-2">
              Điều lệ: thi đấu &gt; 8 trận; 70/80/90% thắng +50/+60/+80; 70/80/90% thua -50/-60/-80.
              VĐV cũ tính mùa hiện tại; VĐV mới tính cả lịch sử. Đôi tính 0,5 trận. Mỗi VĐV chỉ áp dụng một lần/mùa.
            </div>

            {!adjustmentSeason ? (
              <p className="text-muted">Chọn mùa giải để xem danh sách.</p>
            ) : adjustmentLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
              </div>
            ) : (
              <Tabs
                activeKey={adjustmentStatsTab}
                onSelect={(k) => {
                  setAdjustmentStatsTab(k || "old_players");
                  setSelectedAdjustmentIds(new Set());
                }}
                className="mb-3"
              >
                {[
                  ["old_players", "VĐV cũ", "Đủ 10 trận lịch sử; % tính mùa hiện tại"],
                  ["new_players", "VĐV mới", "Từ >8 đến <10 trận lịch sử; % tính cả lịch sử"],
                ].map(([tabKey, label, note]) => (
                  <Tab
                    eventKey={tabKey}
                    title={`${label} (${playerAdjustmentStats[tabKey].length})`}
                    key={tabKey}
                  >
                    <div className="text-muted small mb-2">{note}</div>
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm" className="align-middle">
                        <thead>
                          <tr>
                            <th style={{ width: 40 }}>
                              <Form.Check
                                type="checkbox"
                                checked={allEligibleSelected}
                                disabled={eligibleAdjustmentPlayers.length === 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedAdjustmentIds(
                                      new Set(
                                        eligibleAdjustmentPlayers.map((p) => Number(p.id)),
                                      ),
                                    );
                                  } else {
                                    setSelectedAdjustmentIds(new Set());
                                  }
                                }}
                                aria-label="Chọn tất cả"
                              />
                            </th>
                            <th>VĐV</th>
                            <th>Đội</th>
                            <th>Serie</th>
                            <th>Điểm</th>
                            <th>Trận</th>
                            <th>Thắng</th>
                            <th>Thua</th>
                            <th>Gợi ý</th>
                            <th>Trạng thái</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {playerAdjustmentStats[tabKey].map((player) => {
                            const eligible = !player.not_ready_yet;
                            const pid = Number(player.id);
                            const infoParts = [];
                            if (player.already_applied) {
                              infoParts.push(`Đã áp dụng ${player.adjustment_cycle || 1} lần`);
                              if (player.matches_since_last !== undefined && player.matches_since_last < 8) {
                                infoParts.push(`còn ${8 - Math.floor(player.matches_since_last)} trận nữa`);
                              }
                            }
                            return (
                              <tr
                                key={`${player.id}-${player.team_id}`}
                                className={player.not_ready_yet ? "table-secondary" : undefined}
                              >
                                <td>
                                  <Form.Check
                                    type="checkbox"
                                    checked={selectedAdjustmentIds.has(pid)}
                                    disabled={!eligible || adjustmentApplying}
                                    onChange={(e) =>
                                      toggleAdjustmentSelection(pid, e.target.checked)
                                    }
                                  />
                                </td>
                                <td>{player.name}</td>
                                <td>{player.team_name}</td>
                                <td>{player.level_team_name}</td>
                                <td>{Number(player.level_point ?? 0)}</td>
                                <td>{formatMatchCount(player.total_matches)}</td>
                                <td>
                                  {formatMatchCount(player.wins)} ({formatPercent(player.win_rate)})
                                </td>
                                <td>
                                  {formatMatchCount(player.losses)} ({formatPercent(player.loss_rate)})
                                </td>
                                <td>
                                  <Badge
                                    bg={
                                      player.suggested_adjustment > 0 ? "success" : "danger"
                                    }
                                  >
                                    {player.suggested_adjustment > 0 ? "+" : ""}
                                    {player.suggested_adjustment}
                                  </Badge>
                                </td>
                                <td>
                                  {player.not_ready_yet ? (
                                    <Badge bg="secondary">
                                      {infoParts.join(', ')}
                                    </Badge>
                                  ) : player.already_applied ? (
                                    <Badge bg="info">
                                      {infoParts.join(', ')}
                                    </Badge>
                                  ) : (
                                    <Badge bg="light" text="dark">
                                      Chưa áp dụng
                                    </Badge>
                                  )}
                                </td>
                                <td>
                                  <Button
                                    variant={player.suggested_adjustment > 0 ? "success" : "danger"}
                                    size="sm"
                                    disabled={!eligible || adjustmentApplying}
                                    onClick={() => applyWinrateAdjustments([player.id])}
                                  >
                                    Áp dụng
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                          {playerAdjustmentStats[tabKey].length === 0 && (
                            <tr>
                              <td colSpan={11} className="text-center text-muted">
                                Không có VĐV đủ điều kiện.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Tab>
                ))}
              </Tabs>
            )}

            {/* Lịch sử điều chỉnh */}
            <h6 className="mt-4 mb-2 text-primary">📜 Lịch sử thưởng/phạt % thắng/thua</h6>
            {adjustmentHistoryLoading ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : adjustmentHistory.length === 0 ? (
              <p className="text-muted small">Chưa có lịch sử điều chỉnh cho mùa này.</p>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>VĐV</th>
                      <th>Đội</th>
                      <th>Serie</th>
                      <th>Điểm trước</th>
                      <th>Điều chỉnh</th>
                      <th>Điểm sau</th>
                      <th>Trận</th>
                      <th>TL thắng</th>
                      <th>Cycle</th>
                      <th>Lúc</th>
                      <th>Người thực hiện</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adjustmentHistory.map((r) => (
                      <tr key={r.id}>
                        <td>{r.id}</td>
                        <td>{r.player_name}</td>
                        <td>{r.team_name || "—"}</td>
                        <td>{r.level_team_name || "—"}</td>
                        <td>{Number(r.before_point || 0)}</td>
                        <td>
                          <Badge bg={r.adjustment > 0 ? "success" : "danger"}>
                            {r.adjustment > 0 ? "+" : ""}{r.adjustment}
                          </Badge>
                        </td>
                        <td>{Number(r.after_point || 0)}</td>
                        <td>{formatMatchCount(r.total_matches)}</td>
                        <td>{formatPercent(r.win_rate)}</td>
                        <td>{`Lần ${r.adjustment_cycle}`}</td>
                        <td className="small">
                          {new Date(r.created_at).toLocaleDateString("vi-VN")}
                        </td>
                        <td className="small">{r.applied_by || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Tab>
        )}
      </Tabs>

      {/* Modal thêm/sửa vận động viên */}
      <Modal
        show={showModal}
        onHide={handleClose}
        size="lg"
        fullscreen="md-down"
        className="player-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? "Chỉnh sửa vận động viên" : "Thêm vận động viên mới"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            {/* Thông tin cơ bản */}
            <h6 className="mb-3 text-primary">📋 Thông tin cơ bản</h6>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    👤 Tên <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={currentPlayer.name}
                    onChange={handleFormChange}
                    required
                    placeholder="Nhập tên vận động viên"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>📅 Năm sinh</Form.Label>
                  <Form.Control
                    type="text"
                    name="date_of_birth"
                    value={currentPlayer.date_of_birth}
                    onChange={handleFormChange}
                    placeholder="VD: 1990"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>📱 Điện thoại</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={currentPlayer.phone}
                    onChange={handleFormChange}
                    placeholder="VD: 0912345678"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Hạng */}
            {!isEditing && (
              <Form.Group className="mb-3">
                <Form.Label>🏆 Hạng</Form.Label>
                <Form.Select
                  name="level_start"
                  value={currentPlayer.level_start}
                  onChange={handleLevelStartChange}
                >
                  {Object.entries(LEVEL_MAP).map(([id, { label }]) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {isEditing && (
              <>
                <hr className="my-4" />
                <h6 className="mb-3 text-primary">⚡ Hạng & Điểm</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>🏆 Hạng hiện tại</Form.Label>
                      <Form.Select
                        name="level_present"
                        value={currentPlayer.level_present}
                        onChange={handleLevelPresentChange}
                      >
                        {Object.entries(LEVEL_MAP).map(([id, { label }]) => (
                          <option key={id} value={id}>
                            {label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {user && user.role === "admin" && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>🎯 Điểm hạng</Form.Label>
                        <div className="d-flex align-items-center gap-2">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              const newPoints = Math.max(0, (currentPlayer.level_point || 0) - 1);
                              setCurrentPlayer(prev => ({ ...prev, level_point: newPoints }));
                            }}
                          >
                            ➖
                          </Button>
                          <Form.Control
                            type="number"
                            name="level_point"
                            value={currentPlayer.level_point || 0}
                            onChange={handleFormChange}
                            style={{ width: "80px", textAlign: "center" }}
                            step="0.01"
                          />
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => {
                              const newPoints = (currentPlayer.level_point || 0) + 1;
                              setCurrentPlayer(prev => ({ ...prev, level_point: newPoints }));
                            }}
                          >
                            ➕
                          </Button>
                        </div>
                        <Form.Text className="text-muted d-block mt-1">
                          Đạt 100 điểm → xét lên hạng
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              </>
            )}

            {/* Upload ảnh - chỉ khi tạo mới */}
            {!isEditing && (
              <>
                <hr className="my-4" />
                <h6 className="mb-3 text-primary">
                  📸 Upload ảnh <span className="text-danger">*</span>
                </h6>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ảnh chân dung</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handlePortraitChange}
                        required={!isEditing}
                      />
                      {portraitPreview && (
                        <div className="mt-3 text-center">
                          <img
                            src={portraitPreview}
                            alt="Preview chân dung"
                            className="img-thumbnail"
                            style={{
                              maxWidth: "200px",
                              maxHeight: "200px",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ảnh CCCD</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleIdCardChange}
                        required={!isEditing}
                      />
                      {idCardPreview && (
                        <div className="mt-3 text-center">
                          <img
                            src={idCardPreview}
                            alt="Preview CCCD"
                            className="img-thumbnail"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "200px",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}

            {/* Hiển thị và upload ảnh khi edit */}
            {isEditing && (
              <>
                <hr className="my-4" />
                <h6 className="mb-3 text-primary">📸 Ảnh</h6>

                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ảnh chân dung</Form.Label>
                      {currentPlayer.image && !portraitPreview && (
                        <div className="text-center mb-2">
                          <img
                            src={`${S3_BASE_URL}/player/${currentPlayer.image}`}
                            alt="Ảnh chân dung hiện tại"
                            className="img-thumbnail"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "150px",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/150?text=No+Image";
                            }}
                          />
                          <div className="text-muted small mt-1">Ảnh hiện tại</div>
                        </div>
                      )}
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handlePortraitChange}
                        size="sm"
                      />
                      {portraitPreview && (
                        <div className="mt-2 text-center">
                          <img
                            src={portraitPreview}
                            alt="Preview mới"
                            className="img-thumbnail"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "150px",
                              objectFit: "cover",
                            }}
                          />
                          <div className="text-success small mt-1">✓ Ảnh mới</div>
                        </div>
                      )}
                      {!currentPlayer.image && !portraitPreview && (
                        <p className="text-muted small mt-1">Chưa có ảnh</p>
                      )}
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ảnh CCCD</Form.Label>
                      {currentPlayer.vertical_image && !idCardPreview && (
                        <div className="text-center mb-2">
                          <img
                            src={`${S3_BASE_URL}/player/${currentPlayer.vertical_image}`}
                            alt="Ảnh CCCD hiện tại"
                            className="img-thumbnail"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "150px",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/300x150?text=No+Image";
                            }}
                          />
                          <div className="text-muted small mt-1">Ảnh hiện tại</div>
                        </div>
                      )}
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleIdCardChange}
                        size="sm"
                      />
                      {idCardPreview && (
                        <div className="mt-2 text-center">
                          <img
                            src={idCardPreview}
                            alt="Preview mới"
                            className="img-thumbnail"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "150px",
                              objectFit: "cover",
                            }}
                          />
                          <div className="text-success small mt-1">✓ Ảnh mới</div>
                        </div>
                      )}
                      {!currentPlayer.vertical_image && !idCardPreview && (
                        <p className="text-muted small mt-1">Chưa có ảnh</p>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}

            <div className="d-flex gap-2">
              <Button variant="primary" type="submit">
                {isEditing ? "💾 Cập nhật" : "➕ Thêm mới"}
              </Button>
              <Button variant="secondary" onClick={handleClose}>
                Đóng
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PlayerList;
